import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, LockKeyhole } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { env } from '@/app/config/env';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { getErrorMessage } from '@/shared/api/api-error';
import { Logo } from '@/shared/components/brand/logo';

import { useAuth, useLogin } from '../hooks/use-auth';
import { loginSchema, type LoginFormValues } from '../schemas/login.schema';

/**
 * The one screen that wears the brand loudly (§83).
 *
 * The gradient panel carries the Digital Codex identity; the form itself stays
 * neutral and high-contrast so it remains readable. On small screens the panel
 * collapses to a thin top border — the gradient still signs the page without
 * consuming half of it.
 */
export function LoginPage() {
  const { isAuthenticated, endReason } = useAuth();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { userName: '', password: '' },
    mode: 'onSubmit',
  });

  // Send the user back to whatever they were trying to reach before the guard
  // bounced them here. `/` resolves to the first screen their permissions allow.
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = (values: LoginFormValues) => {
    login.mutate(values, {
      onSuccess: () => navigate(from, { replace: true }),
    });
  };

  return (
    <div className="bg-background grid min-h-svh lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      {/* Brand panel */}
      <div className="bg-brand-gradient relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12">
        {/* A soft radial wash stops the gradient reading as a flat block. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(circle at 25% 15%, rgba(255,255,255,0.45), transparent 55%)',
          }}
        />
        <div className="relative">
          <Logo variant="mark-white" height={44} />
        </div>
        <div className="relative max-w-md text-white">
          <h1 className="font-display text-3xl leading-tight font-bold tracking-tight">
            Digital Codex Administration
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-white/80">
            Manage the catalogue, vendors, orders and configuration behind the Digital Codex voucher
            platform.
          </p>
        </div>
        <p className="relative text-xs text-white/60">
          Authorised personnel only. All activity is logged.
        </p>
      </div>

      {/* Form panel */}
      <div className="flex flex-col">
        {/* Mobile-only brand signature. */}
        <div className="bg-brand-gradient h-1 lg:hidden" aria-hidden />

        <div className="flex flex-1 items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-sm">
            <div className="lg:hidden">
              <Logo height={36} />
            </div>

            <div className="mt-8 lg:mt-0">
              <h2 className="font-display text-2xl font-bold tracking-tight">Sign in</h2>
              <p className="text-muted-foreground mt-1.5 text-sm">
                Use your Digital Codex staff account.
              </p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-5">
                <FormField
                  control={form.control}
                  name="userName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username, email or mobile</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoComplete="username"
                          autoFocus
                          disabled={login.isPending}
                          placeholder="admin"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="password"
                          autoComplete="current-password"
                          disabled={login.isPending}
                          placeholder="••••••••"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Why the previous session ended, until the next attempt. */}
                {endReason && !login.isError && (
                  <div
                    role="status"
                    className="bg-muted text-muted-foreground flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs font-medium"
                  >
                    <LockKeyhole className="mt-px size-3.5 shrink-0" aria-hidden />
                    <span>
                      {endReason === 'idle'
                        ? `You were signed out after ${env.idleTimeoutMinutes} ${env.idleTimeoutMinutes === 1 ? 'minute' : 'minutes'} of inactivity. Please sign in again.`
                        : 'Your session has ended. Please sign in again.'}
                    </span>
                  </div>
                )}

                {/* Server-side failures render inline. No toast as well — one
                    error, one place (§66). */}
                {login.isError && (
                  <div
                    role="alert"
                    className="border-destructive/30 bg-destructive/10 text-destructive flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs font-medium"
                  >
                    <LockKeyhole className="mt-px size-3.5 shrink-0" aria-hidden />
                    <span>{getErrorMessage(login.error, 'Unable to sign in.')}</span>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={login.isPending}>
                  {login.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                  {login.isPending ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
